import React, { useEffect, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import moment from 'moment-timezone';

const TimezoneDatePickerWithTime = ({ value, onChange, timezone, timeOnly = false, ...rest }) => {
  const [displayValue, setDisplayValue] = useState(null);

  /**
   * Convert TZ date → browser-local Date for Calendar
   */
  const toDisplay = (date) => {
    if (!date) return null;

    // Parse the date in the target timezone
    const m = moment.tz(date, timezone);

    if (timeOnly) {
      // For timeOnly: extract the time from the target timezone
      // and create a display Date using today's date
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth(), today.getDate(), m.hours(), m.minutes(), m.seconds(), m.milliseconds());
    }

    // IMPORTANT: create browser-local Date using wall-clock values
    return new Date(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second(), m.millisecond());
  };

  /**
   * Convert Calendar-picked Date → TZ Date
   */
  const fromDisplay = (date) => {
    if (!date) return null;

    if (timeOnly) {
      // For timeOnly: interpret the picked time in the target timezone
      // Use today's date in the target timezone as reference
      const todayInTz = moment.tz(timezone);
      const result = moment.tz(
        {
          year: todayInTz.year(),
          month: todayInTz.month(),
          date: todayInTz.date(),
          hour: date.getHours(),
          minute: date.getMinutes(),
          second: date.getSeconds(),
          millisecond: date.getMilliseconds(),
        },
        timezone
      );

      return result.toDate();
    }

    // Extract wall-clock from browser date
    const parts = {
      year: date.getFullYear(),
      month: date.getMonth(),
      date: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
      millisecond: date.getMilliseconds(),
    };

    return moment.tz(parts, timezone).toDate();
  };

  // Sync incoming value → Calendar
  useEffect(() => {
    if (!value) {
      setDisplayValue(null);
      return;
    }

    if (Array.isArray(value)) {
      setDisplayValue(value.map(toDisplay));
    } else {
      setDisplayValue(toDisplay(value));
    }
  }, [value, timezone, timeOnly]);

  const handleChange = (e) => {
    const picked = e.value;

    if (!picked) {
      onChange({ ...e, value: null, target: { ...e.target, value: null } });
      return;
    }

    let convertedValue;
    if (Array.isArray(picked)) {
      convertedValue = picked.map(fromDisplay);
    } else {
      convertedValue = fromDisplay(picked);
    }

    onChange({
      ...e,
      value: convertedValue,
      target: { ...e.target, value: convertedValue },
    });
  };

  return <Calendar value={displayValue} onChange={handleChange} timeOnly={timeOnly} {...rest} />;
};

export default TimezoneDatePickerWithTime;
